import { QueryKey, UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { BasicEmployee, Employee } from 'src/types';
import { queryClient } from '..';
import {
  EMPLOYEE_PREFERENCES_QUERY_KEY,
  EMPLOYEE_QUERY_KEY,
  ADMIN_EMPLOYEE_KEY,
  BASIC_EMPLOYEE_KEY,
  DEFAULT_ROOM_OPTION_LIST_QUERY_KEY,
  DELETE_KEY,
  ENTITY_KEY,
  LIST_KEY,
  POST_KEY,
  PUT_KEY,
  ROOM_OPTION_LIST_QUERY_KEY,
} from '../queryKeys';
import { readJsonBody } from '../apiStatus';
import {
  invalidateIfAllMutationsDone,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from '../queryYemboApiCalls';
import { invalidateAllEmployeeListsQueries } from '../utils';

const URL = 'employee';

const basicEmployeeFields: (keyof BasicEmployee)[] = ['givenName', 'familyName', 'email', 'companyKeys'] as const;

type GetAllEmployeesParams = {
  signal?: AbortSignal;
  fields?: string[];
  shouldLoadSuperadmins?: boolean;
  companyKey?: string;
};

const getAllEmployees = async <T = BasicEmployee>({
  signal,
  fields = basicEmployeeFields,
  shouldLoadSuperadmins = false,
  companyKey,
}: GetAllEmployeesParams) => {
  const urlParams = new URLSearchParams();

  if (fields) urlParams.set('fields', JSON.stringify(fields));
  if (shouldLoadSuperadmins) urlParams.set('loadSuperadmins', 'true');
  if (companyKey) urlParams.set('companyKey', companyKey);

  const employees = await queryYemboApiCallWithReturnValue<T[], 'employees'>({
    url: URL,
    urlParams,
    key: 'employees',
    shouldNotifyAndThrowError: true,
    signal,
  });

  if (!employees) throw new Error('Employees not found');

  return employees;
};

type AllBasicEmployeesQueryOptions = UseQueryOptions<BasicEmployee[]> & {
  companyKey?: string;
};

export const getAllBasicEmployeesQueryOptions = (
  params?: Partial<AllBasicEmployeesQueryOptions>
): UseQueryOptions<BasicEmployee[]> => {
  const { companyKey, ...options } = params ?? {};

  return {
    queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY, BASIC_EMPLOYEE_KEY, companyKey],
    queryFn: ({ signal }) => getAllEmployees({ signal, companyKey }),
    staleTime: 10 * 60 * 1000, // 10 min because the query may be heavy
    ...options,
  };
};

export const useAllBasicEmployeesQuery = (params?: Partial<AllBasicEmployeesQueryOptions>) =>
  useQuery(getAllBasicEmployeesQueryOptions(params));

/** `GET /employee/:key` answers the employee itself. */
const getEmployee = async (employeeKey: string) => {
  const employee = await queryYemboApiCallWithReturnValue<Employee>({
    url: `${URL}/${employeeKey}`,
    shouldNotifyAndThrowError: true,
    errorOnNotFound: true,
  });

  if (!employee) throw new Error('Employee not found'); // Should never happen because errorOnNotFound is true

  return employee;
};

type EmployeeQueryOptions = Partial<UseQueryOptions<Employee, Error, Employee, string[]>>;

export const getEmployeeQueryOptions = (employeeKey: string | undefined | null, options?: EmployeeQueryOptions) => ({
  queryKey: [EMPLOYEE_QUERY_KEY, ENTITY_KEY, employeeKey ?? ''],
  queryFn: async () => getEmployee(employeeKey ?? ''),
  ...options,
  enabled: options?.enabled !== false && !!employeeKey,
});

export const useEmployeeQuery = (employeeKey: string | undefined | null, options?: EmployeeQueryOptions) =>
  useQuery(getEmployeeQueryOptions(employeeKey, options));

type PartialEmployee<E extends Employee = Employee> = Partial<E> & Pick<E, 'key'> & { confirmPassword?: string };

// service-voyager-api updates an employee at `PUT /employee/:key` — there is no
// keyless `PUT /employee` — and answers the employee. Callers that target one of
// the action endpoints (backup-codes, totp/…) pass their own url and result type.
export const putEmployee = async <E extends Employee = Employee, R = Employee>(
  employee: PartialEmployee<E>,
  url = `employee/${employee.key}`
) => {
  const response = await queryYemboApiCall({
    url,
    method: 'PUT',
    body: JSON.stringify(employee),
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });

  return (await readJsonBody<R>(response)) as R;
};

type PostEmployee = Pick<Employee, 'givenName' | 'familyName' | 'email'> & Partial<Employee>;

const postEmployee = async (employee: PostEmployee) => {
  const response = await queryYemboApiCall({
    url: 'employee',
    method: 'POST',
    body: JSON.stringify(employee),
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });

  return (await response.json()) as Employee;
};

export const usePostEmployee = () => {
  return useMutation({
    mutationKey: [EMPLOYEE_QUERY_KEY, POST_KEY],
    mutationFn: postEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY],
      });
    },
  });
};

type PutEmployeeOptionsContext = {
  prevEmployee: Employee | undefined;
  prevBasicEmployee: BasicEmployee | undefined;
};

type PutEmployeeOptions<E extends Employee = Employee> = UseMutationOptions<
  Employee,
  unknown,
  PartialEmployee<E>,
  PutEmployeeOptionsContext
>;

const setEmployeesQueryData = <BasicEmployeeType extends { key: string }>(
  employee: Partial<BasicEmployeeType>,
  queryKey: string[],
  employeeFields: (keyof BasicEmployeeType)[]
) => {
  const basicEmployee: Partial<BasicEmployeeType> = {};
  basicEmployee.key = employee.key;

  for (const field of employeeFields) {
    if (employee[field] !== undefined) basicEmployee[field] = employee[field];
  }

  let prevBasicEmployee: BasicEmployeeType | undefined;

  queryClient.setQueriesData<BasicEmployeeType[]>({ queryKey }, (employees) =>
    employees?.map((listEmployee) => {
      if (listEmployee.key === basicEmployee.key) {
        prevBasicEmployee = listEmployee;
        return { ...listEmployee, ...basicEmployee };
      }
      return listEmployee;
    })
  );

  return prevBasicEmployee;
};

const restoreEmployeesQueryData = <BasicEmployeeType extends { key: string }>(
  prevBasicEmployee: BasicEmployeeType,
  queryKey: string[]
) => {
  queryClient.setQueriesData<BasicEmployeeType[]>({ queryKey }, (employees) =>
    employees?.map((listEmployee) => {
      if (listEmployee.key === prevBasicEmployee.key) {
        return prevBasicEmployee;
      }
      return listEmployee;
    })
  );
};

const onPutEmployeeMutate = async <E extends Employee = Employee>(employee: PartialEmployee<E>) => {
  await queryClient.cancelQueries({ queryKey: [EMPLOYEE_QUERY_KEY] });

  const queryKey = [EMPLOYEE_QUERY_KEY, ENTITY_KEY, employee.key];

  const prevEmployee = queryClient.getQueryData<Employee>(queryKey);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { confirmPassword, ...employeeWithoutConfirmPassword } = employee;

  if (prevEmployee) {
    queryClient.setQueryData<Employee>(queryKey, {
      ...prevEmployee,
      ...employeeWithoutConfirmPassword,
    });
  }

  const basicEmployee: Partial<BasicEmployee> = {
    key: employee.key,
  };

  for (const field of basicEmployeeFields) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (employee[field] !== undefined) basicEmployee[field] = employee[field];
  }

  const prevBasicEmployee = setEmployeesQueryData<BasicEmployee>(
    employee,
    [EMPLOYEE_QUERY_KEY, LIST_KEY, BASIC_EMPLOYEE_KEY],
    basicEmployeeFields
  );

  return { prevEmployee, prevBasicEmployee };
};

const onPutEmployeeError = <E extends Employee = Employee>(
  error: unknown,
  employee: PartialEmployee<E>,
  context: PutEmployeeOptionsContext | undefined
) => {
  const queryKey = [EMPLOYEE_QUERY_KEY, ENTITY_KEY, employee.key];

  if (context?.prevEmployee) {
    queryClient.setQueryData<Employee>(queryKey, (currentEmployee) =>
      currentEmployee ? context?.prevEmployee : undefined
    );
  }

  if (context?.prevBasicEmployee) {
    restoreEmployeesQueryData<BasicEmployee>(context.prevBasicEmployee, [
      EMPLOYEE_QUERY_KEY,
      LIST_KEY,
      BASIC_EMPLOYEE_KEY,
    ]);
  }
};

export const usePutEmployee = <E extends Employee = Employee>(options?: PutEmployeeOptions<E>) => {
  const mutationKey = [EMPLOYEE_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    mutationFn: (employee: PartialEmployee<E>) => putEmployee(employee),
    ...options,
    onMutate: (employee, context) => {
      const result = onPutEmployeeMutate(employee);
      options?.onMutate?.(employee, context);
      return result;
    },
    onError: (error, employee, onMutateResult, context) => {
      onPutEmployeeError(error, employee, onMutateResult);
      options?.onError?.(error, employee, onMutateResult, context);
    },
    onSettled: (response, error, employee, onMutateResult, context) => {
      invalidateIfAllMutationsDone([EMPLOYEE_QUERY_KEY, ENTITY_KEY, employee.key], mutationKey);
      options?.onSettled?.(response, error, employee, onMutateResult, context);
    },
    onSuccess: (...params) => {
      options?.onSuccess?.(...params);

      const [, { companyKeys, key: employeeKey }, { prevEmployee }] = params;

      const { companyKeys: prevCompanyKeys } = prevEmployee ?? {};

      const hasCompaniesChanged =
        companyKeys &&
        prevCompanyKeys &&
        (companyKeys.length !== prevCompanyKeys.length || companyKeys.some((key) => !prevCompanyKeys.includes(key)));

      if (hasCompaniesChanged) invalidateAllEmployeeListsQueries(employeeKey);
    },
  });
};

/**
 * Upload the signed-in employee's profile photo (already cropped and scaled)
 * to service-voyager-api `PUT /employee/photo`, which stores it and answers the
 * employee itself, with a presigned `photoUrl`.
 */
const putEmployeePhoto = async (photo: Blob) => {
  const form = new FormData();
  form.append('photo', photo, `photo.${photo.type === 'image/png' ? 'png' : 'jpg'}`);

  const response = await queryYemboApiCall({
    url: 'employee/photo',
    method: 'PUT',
    body: form,
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });

  return (await response.json()) as Employee;
};

export const usePutEmployeePhoto = (options?: UseMutationOptions<Employee, Error, Blob>) =>
  useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, PUT_KEY, 'photo'],
    mutationFn: putEmployeePhoto,
    onSuccess: (...params) => {
      const [employee] = params;
      queryClient.setQueryData<Employee>([EMPLOYEE_QUERY_KEY, ENTITY_KEY, employee.key], (current) =>
        current ? { ...current, photoUrl: employee.photoUrl } : current
      );
      options?.onSuccess?.(...params);
    },
  });

export const putEmployeeForcePasswordReset = async (data: { key: string; password: string }) => {
  const response = await queryYemboApiCall({
    url: 'employee/force-password-reset',
    method: 'PUT',
    body: JSON.stringify(data),
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });

  return readJsonBody<unknown>(response);
};

export const postEmployeeNewPassword = async (data: { key: string; password: string }) => {
  const response = await queryYemboApiCall({
    url: 'employee/create-password',
    method: 'POST',
    body: JSON.stringify(data),
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });

  return readJsonBody<unknown>(response);
};

const putEmployeeUnlock = async (key: string) => {
  await queryYemboApiCall({
    url: 'employee/unlock',
    method: 'PUT',
    body: JSON.stringify({ key }),
    shouldNotifyAndThrowError: true,
  });
};

export const usePutEmployeeUnlock = () => {
  const mutationKey = [EMPLOYEE_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    mutationFn: (employee: Pick<Employee, 'key'>) => putEmployeeUnlock(employee.key),

    onMutate: (employee: Pick<Employee, 'key'>) => {
      return onPutEmployeeMutate({
        key: employee.key,
        isSecurityLocked: false,
      });
    },

    onError: onPutEmployeeError,

    onSettled: (response, error, employee) => {
      invalidateIfAllMutationsDone([EMPLOYEE_QUERY_KEY, ENTITY_KEY, employee.key], mutationKey);
    },
  });
};

const deleteEmployee = async ({ key }: { key: string }) => {
  await queryYemboApiCall({
    url: `employee/${key}`,
    method: 'DELETE',
    shouldNotifyAndThrowError: true,
    logoutIfNotAuthorized: false,
  });
};

export const useDeleteEmployee = () => {
  const mutationKey = [EMPLOYEE_QUERY_KEY, DELETE_KEY];

  return useMutation({
    mutationKey,
    mutationFn: deleteEmployee,
    onSuccess: (_, { key }) => {
      queryClient.removeQueries({
        queryKey: [EMPLOYEE_QUERY_KEY, ENTITY_KEY, key],
      });
      queryClient.setQueriesData<Pick<Employee, 'key'>[]>({ queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY] }, (employees) =>
        employees?.filter((employee) => employee.key !== key)
      );
      invalidateAllEmployeeListsQueries(key);
    },
  });
};

export const useGenerateBackupCodes = () => {
  return useMutation({
    mutationKey: [EMPLOYEE_QUERY_KEY, PUT_KEY],
    mutationFn: (key: string) => putEmployee<Employee, { backupCodes: string[] }>({ key }, 'employee/backup-codes'),
    onMutate: (key: string) => {
      let prevBackupCodesGeneratedAt: string | null | undefined;

      queryClient.setQueryData<Employee>([EMPLOYEE_QUERY_KEY, ENTITY_KEY, key], (employee) => {
        prevBackupCodesGeneratedAt = employee?.backupCodesGeneratedAt;
        return (
          employee && {
            ...employee,
            backupCodesGeneratedAt: new Date().toISOString(),
          }
        );
      });

      return prevBackupCodesGeneratedAt;
    },
    onError: (_, key, prevBackupCodesGeneratedAt) => {
      if (prevBackupCodesGeneratedAt === undefined) return;

      queryClient.setQueryData<Employee>(
        [EMPLOYEE_QUERY_KEY, ENTITY_KEY, key],
        (employee) =>
          employee && {
            ...employee,
            backupCodesGeneratedAt: prevBackupCodesGeneratedAt,
          }
      );
    },
    onSuccess(_, key) {
      invalidateIfAllMutationsDone([EMPLOYEE_QUERY_KEY, ENTITY_KEY, key], [EMPLOYEE_QUERY_KEY, PUT_KEY]);
    },
  });
};

type PostEmployeePreferencesParams = {
  employeeKey: string;
  companyKey: string;
  listType?: EmployeePreferencesListType;
};

export const EMPLOYEE_PREFERENCES_LIST_TYPES = ['room-options'] as const;

export type EmployeePreferencesListType = (typeof EMPLOYEE_PREFERENCES_LIST_TYPES)[number];

const postEmployeePreferences = async ({ employeeKey, companyKey, listType }: PostEmployeePreferencesParams) => {
  await queryYemboApiCall({
    url: `${URL}/set-up-employee-preferences`,
    method: 'POST',
    body: JSON.stringify({ employeeKey, companyKey, listType }),
    shouldNotifyAndThrowError: true,
  });
};

export const useCreateEmployeePreferences = (
  options?: UseMutationOptions<void, Error, PostEmployeePreferencesParams>
) => {
  return useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, EMPLOYEE_PREFERENCES_QUERY_KEY, POST_KEY],
    mutationFn: postEmployeePreferences,
    onSuccess: async (...params) => {
      options?.onSuccess?.(...params);

      const [, variables] = params;
      const { employeeKey, listType } = variables;

      const queryKeyMap: Record<string, QueryKey> = {
        'room-options': [ROOM_OPTION_LIST_QUERY_KEY, employeeKey],
        'default-room-options': [DEFAULT_ROOM_OPTION_LIST_QUERY_KEY, employeeKey],
        employee: [EMPLOYEE_QUERY_KEY, ENTITY_KEY, employeeKey],
      };

      let queriesToInvalidate: QueryKey[] = [];
      if (!listType) {
        queriesToInvalidate = Object.values(queryKeyMap);
      } else if (listType === 'room-options') {
        queriesToInvalidate = [queryKeyMap['room-options'], queryKeyMap['default-room-options']];
      } else queriesToInvalidate = [queryKeyMap[listType]];

      await Promise.all(queriesToInvalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
  });
};

/* -------------------------------------------------------------------------
 * Admin → Employees
 *
 * The hooks above describe the signed-in employee (Settings, the assignee
 * picker). These describe *other* people's records, as managed from
 * `/admin/employees`, and read the full row rather than the `BasicEmployee`
 * subset. Company scoping and role guards are enforced by the API — an admin's
 * list already comes back narrowed to their own companies.
 * ---------------------------------------------------------------------- */

const employeeFields: (keyof Employee)[] = [
  'key',
  'givenName',
  'familyName',
  'email',
  'phone',
  'role',
  'companyKeys',
  'createdAt',
] as const;

type EmployeeListParams = {
  /** Narrow to one company. Omitted, the API returns every company the caller can see. */
  companyKey?: string;
  /**
   * Include superadmins, who belong to no company and so match no company
   * filter. The API ignores this for anyone but a superadmin.
   */
  shouldLoadSuperadmins?: boolean;
};

export const getEmployeesQueryOptions = ({ companyKey, shouldLoadSuperadmins }: EmployeeListParams = {}) => ({
  queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY, ADMIN_EMPLOYEE_KEY, companyKey ?? '', String(!!shouldLoadSuperadmins)],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getAllEmployees<Employee>({
      signal,
      companyKey,
      shouldLoadSuperadmins,
      fields: employeeFields as unknown as string[],
    }),
});

export const useEmployeesQuery = (params?: EmployeeListParams) => useQuery(getEmployeesQueryOptions(params));

export type EmployeeInput = Pick<Employee, 'givenName' | 'familyName' | 'email' | 'phone' | 'role' | 'companyKeys'>;

const invalidateEmployeeLists = () =>
  queryClient.invalidateQueries({
    queryKey: [EMPLOYEE_QUERY_KEY, LIST_KEY],
  });

export const useAdminPostEmployee = (options?: UseMutationOptions<Employee, Error, EmployeeInput>) =>
  useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, ADMIN_EMPLOYEE_KEY, POST_KEY],
    mutationFn: (employee: EmployeeInput) => postEmployee(employee as unknown as PostEmployee),
    onSuccess: (...params) => {
      invalidateEmployeeLists();
      options?.onSuccess?.(...params);
    },
  });

type PutEmployeeVariables = Partial<EmployeeInput> & Pick<Employee, 'key'>;

export const useAdminPutEmployee = (options?: UseMutationOptions<Employee, Error, PutEmployeeVariables>) =>
  useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, ADMIN_EMPLOYEE_KEY, PUT_KEY],
    mutationFn: (employee: PutEmployeeVariables) => putEmployee(employee as unknown as PartialEmployee),
    onSuccess: (...params) => {
      const [, { key }] = params;
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY, ENTITY_KEY, key],
      });
      invalidateEmployeeLists();
      options?.onSuccess?.(...params);
    },
  });

export const useAdminDeleteEmployee = (options?: UseMutationOptions<void, Error, Pick<Employee, 'key'>>) =>
  useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, ADMIN_EMPLOYEE_KEY, DELETE_KEY],
    mutationFn: ({ key }: Pick<Employee, 'key'>) => deleteEmployee({ key }),
    onSuccess: (...params) => {
      const [, { key }] = params;
      queryClient.removeQueries({
        queryKey: [EMPLOYEE_QUERY_KEY, ENTITY_KEY, key],
      });
      invalidateEmployeeLists();
      options?.onSuccess?.(...params);
    },
  });

/**
 * Email an employee a password-reset link.
 *
 * `POST /employee` creates an account with no password, and login rejects an
 * account without one — so a newly added employee cannot sign in until they set
 * a password through this link. The endpoint always reports success, whether or
 * not the address is on file, so that it cannot be used to enumerate accounts.
 */
export const useRequestEmployeePasswordReset = (options?: UseMutationOptions<void, Error, { email: string }>) =>
  useMutation({
    ...options,
    mutationKey: [EMPLOYEE_QUERY_KEY, ADMIN_EMPLOYEE_KEY, PUT_KEY, 'password-reset'],
    mutationFn: async ({ email }: { email: string }) => {
      await queryYemboApiCall({
        url: `${URL}/request-password-reset`,
        method: 'PUT',
        body: JSON.stringify({ email }),
        shouldNotifyAndThrowError: true,
        logoutIfNotAuthorized: false,
      });
    },
  });
